<template>
  <div class="w-full" :class="[overrideHeight ? overrideHeight : 'h-[468px]']">
    <div class="flex justify-end">
      <ExportMenu :exports="[{ type: 'chart', action: downloadSVG }]" />
    </div>
    <div ref="chart"></div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import { toProperCase } from "@/utils/common"

export default {
  name: 'PieChart',
  components: { ExportMenu },
  props: {
    overrideHeight: {
      type: String,
      required: false
    },
    chart_data: {
      type: Object,
      required: true,
      default() {
        return {
          values: [25, 30, 45],
          labels: ['Category A', 'Category B', 'Category C'],
          colors: null // Optional custom colors
        }
      }
    },
    chart_config: {
      type: Object,
      default() {
        return {
          margin: { top: 80, right: 80, bottom: 80, left: 80 },
          innerRadius: 0, // 0 for pie, > 0 for donut
          outerRadius: null, // Auto-calculated if null
          padAngle: 0.02,
          cornerRadius: 3,
          labelDisplay: {
            showLabels: true,
            showValues: true,
            minAngleForLabel: 0.05, // Minimum angle (in radians) to show label
            minPercentageForLabel: 5, // Minimum percentage to show label (NEW)
            labelOffset: 20
          },
          legend: {
            display: true,
            position: 'right', // 'right', 'bottom', or 'none'
            spacing: 20
          },
          formatting: {
            percentage: true,
            sig_figs: 1
          },
          title: '',
          subtitle: '',
          tooltip: {
            enabled: true
          }
        }
      }
    }
  },
  emits: ['slice-click'],
  data() {
    return {
      svg: null,
      margin: null,
      colorScale: null
    }
  },
  mounted() {
    this.createChart()
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.createChart()
    },
    formatDataValue(value, total) {
      if (value === null || value === undefined || isNaN(value)) {
        return "N/A"
      }

      let displayValue = value
      if (this.chart_config.formatting.percentage && total) {
        displayValue = (value / total) * 100
      }

      displayValue = displayValue.toFixed(this.chart_config.formatting.sig_figs)

      if (this.chart_config.formatting.percentage) {
        displayValue = displayValue + '%'
      }

      return displayValue
    },
    processData() {
      const total = d3.sum(this.chart_data.values)
      return this.chart_data.labels.map((label, index) => {
        const value = this.chart_data.values[index] || 0
        return {
          label,
          value,
          percentage: (value / total) * 100,
          index,
          color: this.chart_data.colors ? this.chart_data.colors[index] : null
        }
      }).filter(d => d.value > 0) // Filter out zero values
    },
    createChart() {
      d3.select(this.$refs.chart).select('svg').remove()

      const data = this.processData()
      const total = d3.sum(data, d => d.value)

      this.margin = this.chart_config.margin || { top: 80, right: 80, bottom: 80, left: 80 }
      const width = this.$el.clientWidth
      const height = this.$el.clientHeight

      // Adjust margins for a right-side legend. The legend width must scale to
      // the container — a hardcoded 350px squashes the pie to nothing in a
      // narrow (e.g. half-width) card. Cap it at ~38% of width.
      if (this.chart_config.legend.display && this.chart_config.legend.position === 'right') {
        const legendW = this.chart_config.legend.width
        this.margin.right = legendW || Math.min(350, Math.max(140, Math.round(width * 0.38)))
      }

      const chartWidth = width - this.margin.left - this.margin.right
      const chartHeight = height - this.margin.top - this.margin.bottom

      // Create SVG
      const svgElement = d3.select(this.$refs.chart)
        .append('svg')
        .attr('width', width)
        .attr('height', height)

      // Add title with correct font-weight
      if (this.chart_config.title) {
        svgElement.append('text')
          .attr('x', width / 2)
          .attr('y', 30)
          .attr('text-anchor', 'middle')
          .style('font-size', '16px')
          .style('font-weight', '600') // Changed from 'semibold' to '600'
          .text(toProperCase(this.chart_config.title))
      }

      // Add subtitle
      if (this.chart_config.subtitle) {
        svgElement.append('text')
          .attr('x', width / 2)
          .attr('y', 50)
          .attr('text-anchor', 'middle')
          .style('font-size', '14px')
          .style('font-style', 'italic')
          .text(this.chart_config.subtitle)
      }

      const svg = svgElement
        .append('g')
        .attr('transform', `translate(${this.margin.left + chartWidth/2},${this.margin.top + chartHeight/2})`)

      // Calculate radius
      const radius = this.chart_config.outerRadius || Math.min(chartWidth, chartHeight) / 2
      const innerRadius = this.chart_config.innerRadius || 0

      // Create color scale
      const colors = this.chart_data.colors || d3.schemeCategory10
      this.colorScale = d3.scaleOrdinal()
        .domain(data.map(d => d.index))
        .range(colors)

      // Create pie generator
      const pie = d3.pie()
        .value(d => d.value)
        .sort(null) // Keep original order
        .padAngle(this.chart_config.padAngle || 0)

      // Create arc generator
      const arc = d3.arc()
        .innerRadius(innerRadius)
        .outerRadius(radius)
        .cornerRadius(this.chart_config.cornerRadius || 0)

      // Create label arc generator (for positioning labels)
      const labelArc = d3.arc()
        .innerRadius(radius + this.chart_config.labelDisplay.labelOffset)
        .outerRadius(radius + this.chart_config.labelDisplay.labelOffset)

      // Draw slices
      const slices = svg.selectAll('.slice')
        .data(pie(data))
        .enter()
        .append('g')
        .attr('class', 'slice')

      slices.append('path')
        .attr('d', arc)
        .attr('fill', d => d.data.color || this.colorScale(d.data.index))
        .attr('stroke', 'white')
        .attr('stroke-width', 2)
        .style('cursor', 'pointer')
        .on('click', (event, d) => {
          this.$emit('slice-click', d.data)
        })

      // Add external (polyline) labels. These look cluttered in a narrow card,
      // so callers can opt out (externalLabels:false) and rely on the legend +
      // on-hover tooltip instead.
      const externalLabels = this.chart_config.labelDisplay.externalLabels !== false
      if (externalLabels && (this.chart_config.labelDisplay.showLabels || this.chart_config.labelDisplay.showValues)) {
        const minPercentage = this.chart_config.labelDisplay.minPercentageForLabel || 0

        const labels = slices.append('g')
          .attr('class', 'label-group')
          .filter(d => {
            const angleCheck = (d.endAngle - d.startAngle) > this.chart_config.labelDisplay.minAngleForLabel
            const percentageCheck = d.data.percentage >= minPercentage
            return angleCheck && percentageCheck
          })

        // Add label lines
        labels.append('polyline')
          .attr('points', d => {
            const posA = arc.centroid(d)
            const posB = labelArc.centroid(d)
            const posC = labelArc.centroid(d)
            const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2
            posC[0] = radius * 1.2 * (midangle < Math.PI ? 1 : -1)
            return [posA, posB, posC]
          })
          .style('fill', 'none')
          .style('stroke', '#999')
          .style('stroke-width', 1)

        // Add text labels
        const text = labels.append('text')
          .attr('transform', d => {
            const pos = labelArc.centroid(d)
            const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2
            pos[0] = radius * 1.25 * (midangle < Math.PI ? 1 : -1)
            return `translate(${pos})`
          })
          .style('text-anchor', d => {
            const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2
            return midangle < Math.PI ? 'start' : 'end'
          })
          .style('font-size', '12px')

        if (this.chart_config.labelDisplay.showLabels) {
          text.append('tspan')
            .attr('x', 0)
            .attr('y', '-0.3em')
            .style('font-weight', 'normal')
            .text(d => d.data.label)
        }

        if (this.chart_config.labelDisplay.showValues) {
          text.append('tspan')
            .attr('x', 0)
            .attr('y', this.chart_config.labelDisplay.showLabels ? '1em' : '0')
            .style('font-weight', 'bold')
            .text(d => this.formatDataValue(d.data.value, total))
        }
      }

      // Inside-slice labels: percentage drawn centered ON each slice (opt-in
      // via labelDisplay.insideLabels). Hidden for slices too small to fit text.
      if (this.chart_config.labelDisplay.insideLabels) {
        const minPct = this.chart_config.labelDisplay.minPercentageForLabel || 0
        const innerR = innerRadius
        const labelR = innerR > 0 ? (innerR + radius) / 2 : radius * 0.62
        const insideArc = d3.arc().innerRadius(labelR).outerRadius(labelR)
        const showLabel = this.chart_config.labelDisplay.insideShowLabel
        const sig = this.chart_config.formatting.sig_figs ?? 0
        const sliceLabels = svg.selectAll('.inside-label')
          .data(pie(data))
          .enter()
          .filter(d => d.data.percentage >= minPct && (d.endAngle - d.startAngle) > 0.12)
          .append('text')
          .attr('class', 'inside-label')
          .attr('transform', d => `translate(${insideArc.centroid(d)})`)
          .attr('text-anchor', 'middle')
          .style('fill', '#ffffff')
          .style('paint-order', 'stroke')
          .style('stroke', 'rgba(0,0,0,0.30)')
          .style('stroke-width', '2.5px')
          .style('pointer-events', 'none')
        if (showLabel) {
          sliceLabels.append('tspan')
            .attr('x', 0).attr('dy', '-0.2em')
            .style('font-size', '13px').style('font-weight', '600')
            .text(d => d.data.label)
          sliceLabels.append('tspan')
            .attr('x', 0).attr('dy', '1.3em')
            .style('font-size', '15px').style('font-weight', '700')
            .text(d => `${d.data.percentage.toFixed(sig)}%`)
        } else {
          sliceLabels
            .attr('dy', '0.35em')
            .style('font-size', '15px').style('font-weight', '700')
            .text(d => `${d.data.percentage.toFixed(sig)}%`)
        }
        void sliceLabels
      }

      // Add tooltip
      if (this.chart_config.tooltip.enabled) {
        this.addTooltip(slices, total)
      }

      // Add legend
      if (this.chart_config.legend.display) {
        this.addLegend(svgElement, data, width, height)
      }

      this.svg = svg
    },
    addTooltip(slices, total) {
      const tooltip = d3.select(this.$refs.chart)
        .append('div')
        .attr('class', 'tooltip')
        .style('position', 'absolute')
        .style('visibility', 'hidden')
        .style('background', '#fff')
        .style('border', '1px solid #ccc')
        .style('padding', '8px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('border-radius', '4px')
        .style('box-shadow', '0px 2px 4px rgba(0,0,0,0.1)')

      slices.select('path')
        .on('mouseover', (event, d) => {
          const percentage = ((d.data.value / total) * 100).toFixed(1)
          tooltip
            .style('visibility', 'visible')
            .html(`<strong>${d.data.label}</strong><br/>
                   Value: ${this.formatDataValue(d.data.value)}<br/>
                   Percentage: ${percentage}%`)

          // Highlight slice
          d3.select(event.currentTarget)
            .transition()
            .duration(200)
            .attr('transform', function(d) {
              const [x, y] = d3.arc()
                .innerRadius(0)
                .outerRadius(0)
                .centroid(d)
              return `translate(${x * 0.1},${y * 0.1})`
            })
            .style('filter', 'brightness(1.1)')
        })
        .on('mousemove', (event) => {
          tooltip
            .style('top', `${event.pageY - 10}px`)
            .style('left', `${event.pageX + 10}px`)
        })
        .on('mouseout', (event) => {
          tooltip.style('visibility', 'hidden')

          // Remove highlight
          d3.select(event.currentTarget)
            .transition()
            .duration(200)
            .attr('transform', 'translate(0,0)')
            .style('filter', null)
        })
    },
    // When the slice values aren't drawn on the pie (externalLabels:false),
    // fold the value/percentage into the legend so it stays self-describing.
    legendText(d) {
      const total = d3.sum(this.chart_data.values)
      if (this.chart_config.legend.showValue === false) return d.label
      return `${d.label} — ${this.formatDataValue(d.value, total)}`
    },
    addLegend(svg, data, width, height) {
      if (this.chart_config.legend.position === 'right') {
        const legend = svg.selectAll('.legend')
          .data(data)
          .enter()
          .append('g')
          .attr('class', 'legend')
          .attr('transform', (d, i) => `translate(${width - this.margin.right + 20}, ${this.margin.top + i * this.chart_config.legend.spacing})`)

        legend.append('rect')
          .attr('width', 18)
          .attr('height', 18)
          .attr('rx', 3)
          .style('fill', d => d.color || this.colorScale(d.index))

        legend.append('text')
          .attr('x', 26)
          .attr('y', 9)
          .attr('dy', '.35em')
          .style('text-anchor', 'start')
          .style('font-size', '13px')
          .style('fill', '#374151')
          .text(d => this.legendText(d))
      } else if (this.chart_config.legend.position === 'bottom') {
        const legend = svg.selectAll('.legend')
          .data(data)
          .enter()
          .append('g')
          .attr('class', 'legend')

        legend.append('rect')
          .attr('width', 18)
          .attr('height', 18)
          .style('fill', d => d.color || this.colorScale(d.index))

        legend.append('text')
          .attr('x', 24)
          .attr('y', 9)
          .attr('dy', '.35em')
          .style('text-anchor', 'start')
          .style('font-size', '12px')
          .text(d => d.label)

        // Calculate positions for horizontal layout
        let xOffset = 0
        const textWidths = []

        legend.each(function() {
          const text = d3.select(this).select('text')
          const textWidth = text.node().getBBox().width + 35
          textWidths.push(textWidth)
          xOffset += textWidth
        })

        const startX = (width - xOffset) / 2
        xOffset = 0

        legend.attr('transform', function(d, i) {
          const transform = `translate(${startX + xOffset}, ${height - 40})`
          xOffset += textWidths[i]
          return transform
        })
      }
    },
    downloadSVG() {
      const svgElement = this.$refs.chart.querySelector('svg')
      const serializer = new XMLSerializer()
      let source = serializer.serializeToString(svgElement)

      if (!source.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
        source = source.replace(
          /^<svg/,
          '<svg xmlns="http://www.w3.org/2000/svg"'
        )
      }

      const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'pie_chart.svg'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
.tooltip {
  pointer-events: none;
}
</style>
